import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import FolderIcon from '@mui/icons-material/Folder';
import { ProjectIcon, PROJECT_ICONS, PROJECT_COLORS } from './projectAppearance';
import CreateProjectModal from './CreateProjectModal';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Menu from '@mui/material/Menu';
import Collapse from '@mui/material/Collapse';
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE } from '../../shared/Constants';
import { savedReasoningEffort } from '../../utils/chatModelCapabilities';

interface Project {
  project_id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  instructions?: string;
  created_at: string;
  updated_at: string;
}

interface ChatItem {
  chat_id: string;
  title: string;
  updated_at: string;
}

interface ProjectsViewProps {
  onClose: () => void;
  onNavigateToChat: (chatId: string) => void;
  onNewChatInProject: (projectId: string) => void;
  userChats: any[];
}

// ============ Styled Components ============

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 32px 64px;
  min-height: calc(100vh - 120px);
`;

const BackButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  margin-bottom: 18px;
  &:hover { color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 1rem; }
`;

const IconBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.2rem; }
`;

// ===== List header =====
const ListHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const ListTitle = styled.h1`
  font-size: 1.9rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({ theme }) => theme.font_color};
  margin: 0;
  flex: 1;
`;

const PrimaryButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.88; }
  & svg { font-size: 1.1rem; }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${({ theme }) => theme.hover_overlay_light};
  margin-bottom: 24px;
  & svg { font-size: 1.2rem; color: ${({ theme }) => theme.text_secondary}; }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 0.95rem;
  outline: none;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

// ===== Card grid =====
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
`;

const ProjectCard = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 110px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  padding: 18px 20px;
  cursor: pointer;
  background: transparent;
  transition: all 0.15s ease;
  box-sizing: border-box;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
    border-color: ${({ theme }) => theme.font_color}25;
  }
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 1rem;
  font-weight: 550;
  color: ${({ theme }) => theme.font_color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMenu = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0.55;
  transition: opacity 0.12s ease, background 0.12s ease;
  ${ProjectCard}:hover & { opacity: 1; }
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 1.3rem; }
`;

const CardDescription = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 8px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 12px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const CreateCardInline = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 110px;
  border-radius: 16px;
  border: 1px dashed ${({ theme }) => theme.sidebar_border};
  padding: 18px 20px;
  box-sizing: border-box;
  justify-content: center;
`;

const InlineInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.hover_overlay_light};
  color: ${({ theme }) => theme.font_color};
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 20px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.95rem;
`;

const EmptyStateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 56px 32px;
  margin-top: 8px;
  border: 1px dashed ${({ theme }) => theme.sidebar_border};
  border-radius: 16px;
`;

const EmptyIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.hover_overlay_light};
  & svg { font-size: 1.8rem; color: ${({ theme }) => theme.font_color}; opacity: 0.6; }
`;

const EmptyTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({ theme }) => theme.font_color};
`;

const EmptyText = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_secondary};
  max-width: 420px;
  line-height: 1.5;
  margin-bottom: 4px;
`;

const GhostButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 9px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1rem; }
`;

// ===== Detail: two-column =====
const Breadcrumb = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  margin-bottom: 20px;
  &:hover { color: ${({ theme }) => theme.font_color}; }
  & svg { font-size: 1rem; }
`;

const DetailGrid = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  @media (max-width: 880px) { flex-direction: column; }
`;

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DetailTitle = styled.h1`
  font-size: 1.7rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({ theme }) => theme.font_color};
  margin: 0;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 4px;
`;

const DescriptionInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.9rem;
  outline: none;
  margin-top: 6px;
  padding: 0;
  box-sizing: border-box;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; opacity: 0.6; }
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  border-radius: 16px;
  padding: 14px 16px;
  margin: 22px 0 28px;
  transition: border-color 0.15s ease;
  &:focus-within { border-color: ${({ theme }) => theme.font_color}40; }
`;

const ComposerInput = styled.textarea`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  outline: none;
  min-height: 28px;
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const ComposerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SendButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ theme }) => theme.font_color};
  color: ${({ theme }) => theme.sidebar_background_solid || theme.sidebar_background};
  cursor: pointer;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.35; cursor: default; }
  & svg { font-size: 1.1rem; }
`;

const ConvHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ConvHeader = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const ChatList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  border-top: 1px solid ${({ theme }) => theme.sidebar_border};
  &:last-child { border-bottom: 1px solid ${({ theme }) => theme.sidebar_border}; }
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

const ChatInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatName = styled.div`
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatMeta = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 2px;
`;

const RemoveBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  opacity: 0;
  ${ChatRow}:hover & { opacity: 1; }
  &:hover { color: ${({ theme }) => theme.font_color}; background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1rem; }
`;

const AddChatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  border-top: 1px solid ${({ theme }) => theme.sidebar_border};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
`;

const SearchBoxInline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  margin: 12px 0;
  & svg { font-size: 1.1rem; color: ${({ theme }) => theme.text_secondary}; }
`;

// ===== Detail: right panel =====
const SidePanel = styled.div`
  width: 340px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  border-radius: 16px;
  overflow: hidden;
  align-self: flex-start;
  @media (max-width: 880px) { width: 100%; }
`;

const PanelSection = styled.div`
  padding: 16px 18px;
  & + & { border-top: 1px solid ${({ theme }) => theme.sidebar_border}; }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const PanelHint = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 4px;
  line-height: 1.4;
`;

const PickerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
`;

const IconChoice = styled.button<{ $sel: boolean }>`
  all: unset;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.hover_overlay_light};
  border: 2px solid ${({ $sel, theme }) => ($sel ? theme.font_color : 'transparent')};
  cursor: pointer;
  font-size: 17px;
  color: ${({ theme }) => theme.font_color};
  & svg { font-size: 17px; }
`;

const ColorSwatch = styled.button<{ $sel: boolean; $c: string }>`
  all: unset;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  border: 3px solid ${({ $sel, theme }) => ($sel ? theme.font_color : 'transparent')};
  cursor: pointer;
`;

const InstructionsTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 110px;
  resize: vertical;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  background: transparent;
  color: ${({ theme }) => theme.font_color};
  font-family: inherit;
  font-size: 0.88rem;
  line-height: 1.45;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.font_color}; }
  &::placeholder { color: ${({ theme }) => theme.text_secondary}; }
`;

const MenuItemPopup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.font_color};
  &:hover { background: ${({ theme }) => theme.hover_overlay_light}; }
  & svg { font-size: 1.1rem; }
`;

// ============ Component ============

const skeletonPulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

const SkeletonCard = styled.div`
  min-height: 110px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.sidebar_border};
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;
`;

const SkeletonBar = styled.div<{ $w?: string }>`
  height: 12px;
  width: ${({ $w }) => $w || '100%'};
  border-radius: 6px;
  background: ${({ theme }) => theme.hover_overlay_light};
`;

const ProjectsView: React.FC<ProjectsViewProps> = ({ onClose, onNavigateToChat, onNewChatInProject, userChats }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [projectChats, setProjectChats] = useState<ChatItem[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuProjectId, setMenuProjectId] = useState<string | null>(null);
  const [addSearch, setAddSearch] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [projectChatsCache, setProjectChatsCache] = useState<Record<string, any[]>>({});
  const createRef = useRef<HTMLInputElement>(null);
  const [descDraft, setDescDraft] = useState('');
  const [instrDraft, setInstrDraft] = useState('');
  const [showAddExisting, setShowAddExisting] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/list`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) { const data = await res.json(); setProjects(data.projects || []); }
    } catch (e) { console.error('Error fetching projects:', e); }
    finally { setLoading(false); }
  }, [getAccessTokenSilently]);

  const fetchProjectChats = useCallback(async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/${projectId}/chats`, { headers: { Authorization: `Auth ${token}` } });
      if (res.ok) { const data = await res.json(); setProjectChats(data.chats || []); }
    } catch (e) { console.error('Error fetching project chats:', e); }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Fetch chat counts for each project in the list
  useEffect(() => {
    if (projects.length > 0 && !activeProject) {
      projects.forEach(async (p: any) => {
        try {
          const token = await getAccessTokenSilently();
          const res = await fetch(`${API_BASE}/api/projects/${p.project_id}/chats`, { headers: { Authorization: `Auth ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setProjectChatsCache(prev => ({ ...prev, [p.project_id]: data.chats || [] }));
          }
        } catch (e) { /* ignore */ }
      });
    }
  }, [projects, activeProject, getAccessTokenSilently]);

  // Listen for direct project navigation from sidebar
  useEffect(() => {
    const handler = (e: any) => {
      const projectId = e.detail;
      if (projectId) {
        const project = projects.find((p: any) => p.project_id === projectId);
        if (project) {
          setActiveProject(project);
          fetchProjectChats(project.project_id);
        } else {
          fetchProjects();
        }
      }
    };
    window.addEventListener('navigateToProject', handler);
    return () => window.removeEventListener('navigateToProject', handler);
  }, [projects, fetchProjects, fetchProjectChats]);

  // Listen for "start creating" signal from sidebar "Novo projeto"
  useEffect(() => {
    const handler = () => {
      if (!activeProject) {
        setShowCreateModal(true);
      }
    };
    window.addEventListener('startCreateProject', handler);
    return () => window.removeEventListener('startCreateProject', handler);
  }, [activeProject]);

  // Sync description/instructions drafts when switching projects.
  useEffect(() => {
    setDescDraft(activeProject?.description || '');
    setInstrDraft(activeProject?.instructions || '');
    setShowAddExisting(false);
  }, [activeProject?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/projects/create`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) { setNewName(''); setIsCreating(false); fetchProjects(); toast.success("Projeto criado", { autoClose: 2000 }); window.dispatchEvent(new CustomEvent('projectsChanged')); }
    } catch (e) { console.error('Error creating project:', e); }
  };

  const handleRename = async (projectId: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/rename`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, name }),
      });
      setEditingId(null); toast.success("Projeto renomeado", { autoClose: 2000 });
      fetchProjects(); window.dispatchEvent(new CustomEvent('projectsChanged'));
      if (activeProject?.project_id === projectId) setActiveProject({ ...activeProject, name });
    } catch (e) { console.error('Error renaming:', e); }
  };

  const handleDelete = async (projectId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/delete`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId }),
      });
      setMenuAnchor(null); setMenuProjectId(null);
      if (activeProject?.project_id === projectId) { setActiveProject(null); setProjectChats([]); } toast.success("Projeto excluído", { autoClose: 2000 });
      fetchProjects(); window.dispatchEvent(new CustomEvent("projectsChanged"));
    } catch (e) { console.error('Error deleting:', e); }
  };

  // Autosave description / instructions / appearance.
  const handleSaveProject = async (fields: Partial<Project>) => {
    if (!activeProject) return;
    const optimistic = { ...activeProject, ...fields };
    setActiveProject(optimistic);
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/projects/update`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: activeProject.project_id, ...fields }),
      });
      fetchProjects();
      window.dispatchEvent(new CustomEvent('projectsChanged'));
    } catch (e) { console.error('Error updating project:', e); }
  };

  const handleAddChat = async (chatId: string) => {
    if (!activeProject) return;
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: activeProject.project_id }),
      });
      fetchProjectChats(activeProject.project_id); window.dispatchEvent(new CustomEvent("projectsChanged"));
    } catch (e) { console.error('Error adding chat:', e); }
  };

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const handleRemoveChat = async (chatId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: null }),
      });
      setConfirmRemove(null);
      if (activeProject) fetchProjectChats(activeProject.project_id);
    } catch (e) { console.error('Error removing chat:', e); }
  };

  const [newChatMessage, setNewChatMessage] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);

  const handleNewChatSubmit = async () => {
    if (!activeProject || !newChatMessage.trim() || creatingChat) return;
    const message = newChatMessage.trim();
    setCreatingChat(true);
    try {
      const token = await getAccessTokenSilently();
      const createRes = await fetch(`${API_BASE}/api/chat/create`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        // Only a deliberate browser preference; without one the server
        // initializes the chat with the plan's default on the first turn.
        body: JSON.stringify({ reasoning_effort: savedReasoningEffort() }),
      });
      if (!createRes.ok) { setCreatingChat(false); return; }
      const createData = await createRes.json();
      const chatId = createData.chat_data.chat_id;

      await fetch(`${API_BASE}/api/chat/move-to-project`, {
        method: 'POST', headers: { Authorization: `Auth ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, project_id: activeProject.project_id }),
      });

      setNewChatMessage('');
      setCreatingChat(false);
      window.dispatchEvent(new CustomEvent('projectsChanged'));
      window.dispatchEvent(new CustomEvent('addChatToList', { detail: createData.chat_data }));

      onNavigateToChat(chatId);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('prefillChatMessage', { detail: message }));
      }, 300);
    } catch (e) {
      console.error('Error creating chat in project:', e);
      setCreatingChat(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
  };

  const projectChatIds = new Set(projectChats.map(c => c.chat_id));
  const filteredAddChats = addSearch
    ? userChats.filter((c: any) => !projectChatIds.has(c.chat_id) && (c.title || '').toLowerCase().includes(addSearch.toLowerCase()))
    : userChats.filter((c: any) => !projectChatIds.has(c.chat_id));

  const startCreate = () => { setShowCreateModal(true); };

  // ============ PROJECT LIST VIEW ============
  if (!activeProject) {
    const visibleProjects = listSearch
      ? projects.filter(p => (p.name || '').toLowerCase().includes(listSearch.toLowerCase()))
      : projects;

    return (
      <Container>
        <ListHeader>
          <ListTitle>Projetos</ListTitle>
          {/* O botão do topo some quando a tela vazia mostra seu próprio CTA */}
          {!(!loading && projects.length === 0 && !listSearch) && (
            <PrimaryButton onClick={startCreate}><AddIcon /> Novo projeto</PrimaryButton>
          )}
        </ListHeader>

        <SearchBar>
          <SearchIcon />
          <SearchInput
            placeholder="Procurar projetos..."
            value={listSearch}
            onChange={(e) => setListSearch(e.target.value)}
          />
        </SearchBar>

        {loading ? (
          <CardGrid>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i}>
                <SkeletonBar $w="55%" />
                <SkeletonBar $w="88%" />
                <SkeletonBar $w="40%" style={{ marginTop: 'auto' }} />
              </SkeletonCard>
            ))}
          </CardGrid>
        ) : (
          <>
        <CardGrid>

          {visibleProjects.map((project) => {
            const chats = projectChatsCache[project.project_id] || [];
            return (
              <ProjectCard key={project.project_id} onClick={() => {
                if (editingId !== project.project_id) {
                  setActiveProject(project);
                  fetchProjectChats(project.project_id);
                }
              }}>
                <CardTopRow>
                  {project.icon ? <ProjectIcon icon={project.icon} color={project.color} size={18} /> : null}
                  {editingId === project.project_id ? (
                    <InlineInput
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRename(project.project_id); if (e.key === 'Escape') setEditingId(null); }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      placeholder="Nome do projeto"
                    />
                  ) : (
                    <>
                      <CardName>{project.name}</CardName>
                      <CardMenu onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuProjectId(project.project_id); }}>
                        <MoreHorizIcon />
                      </CardMenu>
                    </>
                  )}
                </CardTopRow>
                {project.description ? <CardDescription>{project.description}</CardDescription> : null}
                <CardFooter>
                  {chats.length > 0 ? `${chats.length} conversa${chats.length > 1 ? 's' : ''} · ` : ''}
                  Atualizado {formatDate(project.updated_at)}
                </CardFooter>
              </ProjectCard>
            );
          })}
        </CardGrid>

        {visibleProjects.length === 0 && (
          listSearch ? (
            <EmptyState>
              <span>Nenhum projeto encontrado</span>
            </EmptyState>
          ) : (
            <EmptyStateCard>
              <EmptyIconCircle><FolderIcon /></EmptyIconCircle>
              <EmptyTitle>Crie seu primeiro projeto</EmptyTitle>
              <EmptyText>
                Agrupe conversas relacionadas em um projeto para compartilhar contexto
                e definir instruções que o assistente segue em todas elas.
              </EmptyText>
              <PrimaryButton onClick={startCreate}><AddIcon /> Novo projeto</PrimaryButton>
            </EmptyStateCard>
          )
        )}
          </>
        )}

        <Menu
          anchorEl={menuAnchor} open={Boolean(menuAnchor)}
          onClose={() => { setMenuAnchor(null); setMenuProjectId(null); }}
          PaperProps={{ sx: (theme) => ({ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? '#232321' : 'white', color: theme.palette.mode === 'dark' ? 'white' : '#232321', boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)', minWidth: '180px' }) }}
          MenuListProps={{ sx: { padding: '8px' } }} TransitionComponent={Collapse}
        >
          <MenuItemPopup onClick={() => { const p = projects.find(p => p.project_id === menuProjectId); if (p) { setEditingId(p.project_id); setEditName(p.name); } setMenuAnchor(null); setMenuProjectId(null); }}>
            <ModeEditOutlineIcon /> <span>Renomear</span>
          </MenuItemPopup>
          <MenuItemPopup onClick={() => { if (menuProjectId) handleDelete(menuProjectId); }}>
            <DeleteOutlineIcon sx={{ color: '#991b1b' }} /> <span style={{ color: '#991b1b' }}>Apagar</span>
          </MenuItemPopup>
        </Menu>

        <CreateProjectModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={(project) => {
            fetchProjects();
            setActiveProject(project);
            fetchProjectChats(project.project_id);
          }}
        />
      </Container>
    );
  }

  // ============ PROJECT DETAIL VIEW ============
  return (
    <Container>
      <Breadcrumb onClick={() => { setActiveProject(null); setProjectChats([]); }}>
        <ArrowBackIcon /> Todos os projetos
      </Breadcrumb>

      <DetailGrid>
        <MainCol>
          <TitleRow>
            {activeProject.icon ? <ProjectIcon icon={activeProject.icon} color={activeProject.color} size={24} /> : null}
            {editingId === activeProject.project_id ? (
              <>
                <InlineInput
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRename(activeProject.project_id); if (e.key === 'Escape') setEditingId(null); }}
                  autoFocus
                  placeholder="Nome do projeto"
                  style={{ fontSize: '1.4rem', fontWeight: 600 }}
                />
                <IconBtn onClick={() => handleRename(activeProject.project_id)}><CheckIcon /></IconBtn>
                <IconBtn onClick={() => setEditingId(null)}><CloseIcon /></IconBtn>
              </>
            ) : (
              <>
                <DetailTitle>{activeProject.name}</DetailTitle>
                <IconBtn onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuProjectId(activeProject.project_id); }}>
                  <MoreHorizIcon />
                </IconBtn>
              </>
            )}
          </TitleRow>
          <DescriptionInput
            placeholder="Adicione uma descrição ao projeto..."
            value={descDraft}
            maxLength={500}
            onChange={(e) => setDescDraft(e.target.value)}
            onBlur={() => { if (descDraft !== (activeProject?.description || '')) handleSaveProject({ description: descDraft }); }}
          />

          <Composer>
            <ComposerInput
              rows={2}
              placeholder="Como posso ajudar você hoje?"
              value={newChatMessage}
              onChange={(e) => setNewChatMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNewChatSubmit(); } }}
              disabled={creatingChat}
            />
            <ComposerToolbar>
              <span style={{ fontSize: '0.78rem', color: 'inherit', opacity: 0.45 }}>
                {creatingChat ? 'Criando...' : `Nova conversa em ${activeProject.name}`}
              </span>
              <SendButton onClick={handleNewChatSubmit} disabled={!newChatMessage.trim() || creatingChat} aria-label="Iniciar conversa">
                <AddIcon />
              </SendButton>
            </ComposerToolbar>
          </Composer>

          <ConvHeaderRow>
            <ConvHeader>Conversas{projectChats.length > 0 ? ` (${projectChats.length})` : ''}</ConvHeader>
            <GhostButton onClick={() => setShowAddExisting(v => !v)}>
              <AddIcon /> Adicionar existente
            </GhostButton>
          </ConvHeaderRow>

          {showAddExisting && (
            <>
              <SearchBoxInline>
                <SearchIcon />
                <SearchInput
                  placeholder="Buscar conversas para adicionar..."
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  autoFocus
                />
              </SearchBoxInline>
              <ChatList>
                {filteredAddChats.map((chat: any) => (
                  <AddChatRow key={chat.chat_id} onClick={() => handleAddChat(chat.chat_id)}>
                    <ChatInfo>
                      <ChatName>{chat.title || 'Conversa sem título'}</ChatName>
                      <ChatMeta>Última mensagem {formatDate(chat.updated_at)}</ChatMeta>
                    </ChatInfo>
                    <GhostButton><AddIcon /> Adicionar</GhostButton>
                  </AddChatRow>
                ))}
                {filteredAddChats.length === 0 && (
                  <EmptyState style={{ padding: '24px' }}>
                    <span>{addSearch ? 'Nenhuma conversa encontrada' : 'Todas as conversas já estão no projeto'}</span>
                  </EmptyState>
                )}
              </ChatList>
            </>
          )}

          {projectChats.length === 0 ? (
            <EmptyState>
              <span>Nenhuma conversa neste projeto ainda</span>
            </EmptyState>
          ) : (
            <ChatList>
              {projectChats.map((chat) => (
                <ChatRow key={chat.chat_id} onClick={() => onNavigateToChat(chat.chat_id)}>
                  <ChatInfo>
                    <ChatName>{chat.title || 'Conversa sem título'}</ChatName>
                    <ChatMeta>Última mensagem {formatDate(chat.updated_at)}</ChatMeta>
                  </ChatInfo>
                  {confirmRemove === chat.chat_id ? (
                    <>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Remover?</span>
                      <IconBtn onClick={(e) => { e.stopPropagation(); handleRemoveChat(chat.chat_id); }} title="Confirmar">
                        <CheckIcon style={{ fontSize: '0.9rem', color: '#991b1b' }} />
                      </IconBtn>
                      <IconBtn onClick={(e) => { e.stopPropagation(); setConfirmRemove(null); }} title="Cancelar">
                        <CloseIcon style={{ fontSize: '0.9rem' }} />
                      </IconBtn>
                    </>
                  ) : (
                    <RemoveBtn onClick={(e) => { e.stopPropagation(); setConfirmRemove(chat.chat_id); }} title="Remover do projeto">
                      <CloseIcon />
                    </RemoveBtn>
                  )}
                </ChatRow>
              ))}
            </ChatList>
          )}
        </MainCol>

        <SidePanel>
          <PanelSection>
            <PanelHead>Instruções</PanelHead>
            <PanelHint>O assistente segue estas instruções em todas as conversas deste projeto.</PanelHint>
            <InstructionsTextArea
              placeholder="Ex.: Foque em direito tributário e cite sempre a legislação."
              value={instrDraft}
              maxLength={4000}
              onChange={(e) => setInstrDraft(e.target.value.slice(0, 4000))}
              onBlur={() => { if (instrDraft !== (activeProject?.instructions || '')) handleSaveProject({ instructions: instrDraft }); }}
            />
          </PanelSection>

          <PanelSection>
            <PanelHead>Aparência</PanelHead>
            <PanelHint>Ícone</PanelHint>
            <PickerRow>
              <IconChoice $sel={!activeProject.icon} onClick={() => handleSaveProject({ icon: '' })} title="Padrão">
                <ProjectIcon size={16} />
              </IconChoice>
              {PROJECT_ICONS.map((e) => (
                <IconChoice key={e} $sel={activeProject!.icon === e} onClick={() => handleSaveProject({ icon: e })}>{e}</IconChoice>
              ))}
            </PickerRow>
            <PanelHint style={{ marginTop: '12px' }}>Cor</PanelHint>
            <PickerRow>
              {PROJECT_COLORS.map((c) => (
                <ColorSwatch key={c} $c={c} $sel={activeProject!.color === c} onClick={() => handleSaveProject({ color: activeProject!.color === c ? '' : c })} aria-label={`cor ${c}`} />
              ))}
            </PickerRow>
          </PanelSection>

          <PanelSection>
            <PanelHead>Memória do projeto</PanelHead>
            <PanelHint>As conversas deste projeto compartilham contexto entre si: o assistente pode consultar o que foi discutido nas outras conversas para enriquecer as respostas.</PanelHint>
          </PanelSection>
        </SidePanel>
      </DetailGrid>

      <Menu
        anchorEl={menuAnchor} open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuProjectId(null); }}
        PaperProps={{ sx: (theme) => ({ borderRadius: '12px', bgcolor: theme.palette.mode === 'dark' ? '#232321' : 'white', color: theme.palette.mode === 'dark' ? 'white' : '#232321', boxShadow: theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)', minWidth: '180px' }) }}
        MenuListProps={{ sx: { padding: '8px' } }} TransitionComponent={Collapse}
      >
        <MenuItemPopup onClick={() => {
          if (activeProject) { setEditingId(activeProject.project_id); setEditName(activeProject.name); }
          setMenuAnchor(null); setMenuProjectId(null);
        }}>
          <ModeEditOutlineIcon /> <span>Renomear</span>
        </MenuItemPopup>
        <MenuItemPopup onClick={() => { if (menuProjectId) handleDelete(menuProjectId); }}>
          <DeleteOutlineIcon sx={{ color: '#991b1b' }} /> <span style={{ color: '#991b1b' }}>Apagar projeto</span>
        </MenuItemPopup>
      </Menu>
    </Container>
  );
};

export default ProjectsView;
